import './SingleService.scss';

import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { PATH, RouteParams } from 'router';
import { useDataQuery, useGTMDataSend } from 'common/hooks';
import {
  querySingleService,
  mutationMainForm,
  mutationFileForm,
} from 'network';
import { cn } from 'ui/utils/bem';
import { Breadcrumbs } from '@dataartdev/uikit/Breadcrumbs';
import { Typography } from '@dataartdev/uikit/Typography';
import { Grid, GridItem } from '@dataartdev/uikit/Grid';
import { Article } from '../../../common/components/Article/Article';
import { ContentBuilder } from 'common/components/ContentBuilder/ContentBuilder';
import { Picture } from '@dataartdev/uikit/Picture';
import { CtaScrollerBtn } from 'common/components/CtaScrollerBtn';
import { recaptchaKey, recaptchaFormKey } from 'common/constants';
import { useIntl } from 'react-intl';
import { ISingleService } from './models';
import { useRedirect404 } from '../../../common/hooks/useRedirect404';

const cnSingleService = cn('SingleService');

type Props = RouteComponentProps<RouteParams> & {};

const SingleService: React.FC<Props> = ({ match, history }) => {
  const { locale } = useIntl();
  const { data, loading, error } =
    useDataQuery<ISingleService>(querySingleService, {
      slug: match.params.slug,
      locale,
    }) || {};

  const {
    breadcrumbs = [],
    title,
    text,
    headerLink,
    picture,
    content,
    metaTitle,
  } = data || {};

  useRedirect404(error, history);
  useGTMDataSend(metaTitle);

  return (
    <>
      <div className={cnSingleService()}>
        {!loading && (
          <>
            <div className="container">
              {breadcrumbs.length > 0 && (
                <div className={cnSingleService('Breadcrumbs')}>
                  <Breadcrumbs
                    getLabel={page => page.text}
                    getLink={page => page.url}
                    history={history}
                    pages={breadcrumbs}
                  />
                </div>
              )}
              {(title || text || headerLink || picture) && (
                <Grid
                  className={cnSingleService('Head')}
                  breakpoints={{
                    desktop: { cols: 12, gap: 'xl' },
                    tablet: { cols: 8, gap: 'm' },
                  }}
                >
                  <GridItem
                    breakpoints={{ desktop: { col: 8 }, tablet: { col: 5 } }}
                  >
                    {title && (
                      <Typography.Title
                        size="hg"
                        className={cnSingleService('Title')}
                        as="h1"
                      >
                        {title}
                      </Typography.Title>
                    )}
                    {text && (
                      <Article
                        content={text}
                        className={cnSingleService('Text')}
                      />
                    )}
                    {headerLink && (
                      <div className={cnSingleService('Cta')}>
                        <CtaScrollerBtn
                          title={headerLink.title}
                          link={headerLink.link}
                        />
                      </div>
                    )}
                  </GridItem>
                  {picture && (
                    <GridItem
                      breakpoints={{
                        desktop: { col: 2, colStart: 10 },
                        tablet: { col: 3, colStart: 6 },
                      }}
                      className={cnSingleService('Picture')}
                    >
                      <Picture
                        desktop={picture.url}
                        desktopWebp={picture.urlWebp}
                      />
                    </GridItem>
                  )}
                </Grid>
              )}
            </div>
            {content && content.length > 0 && (
              <div className={cnSingleService('Content')}>
                <ContentBuilder
                  items={content}
                  aliases={PATH}
                  recaptchaKey={recaptchaKey}
                  onSubmitForm={async body =>
                    await mutationMainForm({
                      body: { ...body },
                    })
                  }
                  onSubmitFileForm={body =>
                    mutationFileForm({
                      body: {
                        ...body,
                        pageId: data?.id,
                        [`${recaptchaFormKey}`]: body.token,
                      },
                    })
                  }
                  CtaScrollerBtnLink="expert-form-01"
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default SingleService;
